import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AuthenticationResponse } from 'data/schemas/auth';

interface SingleEmployeePickerProps {
    value: string;
    employees: AuthenticationResponse[];
    disabled?: boolean;
    placeholder?: string;
    onChange: (citizenId: string) => void;
}

interface MultiEmployeePickerProps {
    values: string[];
    employees: AuthenticationResponse[];
    disabled?: boolean;
    placeholder?: string;
    onChange: (citizenIds: string[]) => void;
}

function getEmployeeLabel(employee: AuthenticationResponse) {
    const nickName = employee.nickName ?? '';
    const citizenId = employee.ctizenID ?? '';
    return nickName && citizenId ? `${nickName} (${citizenId})` : nickName || citizenId;
}

function usePickerSearch(employees: AuthenticationResponse[], search: string, selectedIds?: string[]) {
    return useMemo(() => {
        const keyword = search.trim().toLowerCase();
        return employees.filter(employee => {
            const citizenId = employee.ctizenID ?? '';
            const nickName = employee.nickName ?? '';
            const isSelected = selectedIds?.includes(citizenId);
            if (isSelected) {
                return false;
            }

            if (!keyword) {
                return true;
            }

            return (
                citizenId.toLowerCase().includes(keyword) || nickName.toLowerCase().includes(keyword)
            );
        });
    }, [employees, search, selectedIds]);
}

function PickerShell({
    disabled,
    children
}: {
    disabled?: boolean;
    children: React.ReactNode;
}) {
    return (
        <div
            className={`rounded-md border border-gray-300 bg-white ${disabled ? 'bg-gray-100' : ''}`}
        >
            {children}
        </div>
    );
}

export function SingleEmployeePicker({
    value,
    employees,
    disabled,
    placeholder,
    onChange
}: SingleEmployeePickerProps) {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const filteredEmployees = usePickerSearch(employees, search);
    const selectedEmployee = employees.find(employee => employee.ctizenID === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div
            ref={containerRef}
            className="relative"
        >
            <PickerShell disabled={disabled}>
                <input
                    value={isOpen ? search : getEmployeeLabel(selectedEmployee ?? { ctizenID: value })}
                    onChange={event => {
                        setSearch(event.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => !disabled && setIsOpen(true)}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="w-full rounded-md bg-transparent px-3 py-2 text-sm outline-none"
                />
            </PickerShell>
            {isOpen && !disabled && (
                <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {filteredEmployees.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-400">No employee found</div>
                    ) : (
                        filteredEmployees.map(employee => (
                            <button
                                key={employee.ctizenID}
                                type="button"
                                onMouseDown={() => {
                                    onChange(employee.ctizenID ?? '');
                                    setIsOpen(false);
                                    setSearch('');
                                }}
                                className="block w-full px-3 py-2 text-left text-sm hover:bg-blue-50"
                            >
                                {getEmployeeLabel(employee)}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

export function MultiEmployeePicker({
    values,
    employees,
    disabled,
    placeholder,
    onChange
}: MultiEmployeePickerProps) {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const filteredEmployees = usePickerSearch(employees, search, values);
    const selectedEmployees = values
        .map(value => employees.find(employee => employee.ctizenID === value))
        .filter(Boolean) as AuthenticationResponse[];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div
            ref={containerRef}
            className="relative"
        >
            <PickerShell disabled={disabled}>
                <div className="flex min-h-[42px] flex-wrap items-center gap-2 px-2 py-2">
                    {selectedEmployees.map(employee => (
                        <span
                            key={employee.ctizenID}
                            className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                        >
                            {getEmployeeLabel(employee)}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        onChange(values.filter(value => value !== employee.ctizenID))
                                    }
                                    className="text-blue-500 hover:text-blue-700"
                                >
                                    x
                                </button>
                            )}
                        </span>
                    ))}
                    <input
                        value={search}
                        onChange={event => {
                            setSearch(event.target.value);
                            setIsOpen(true);
                        }}
                        onFocus={() => !disabled && setIsOpen(true)}
                        placeholder={selectedEmployees.length === 0 ? placeholder : ''}
                        disabled={disabled}
                        className="min-w-[180px] flex-1 bg-transparent px-1 py-1 text-sm outline-none"
                    />
                </div>
            </PickerShell>
            {isOpen && !disabled && (
                <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {filteredEmployees.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-400">No employee found</div>
                    ) : (
                        filteredEmployees.map(employee => (
                            <button
                                key={employee.ctizenID}
                                type="button"
                                onMouseDown={() => {
                                    const citizenId = employee.ctizenID ?? '';
                                    if (!citizenId) {
                                        return;
                                    }
                                    onChange([...values, citizenId]);
                                    setSearch('');
                                    setIsOpen(true);
                                }}
                                className="block w-full px-3 py-2 text-left text-sm hover:bg-blue-50"
                            >
                                {getEmployeeLabel(employee)}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
